import React, { useMemo } from "react";
import { Fulfillment } from "@secondcloset/types";
import { Space, Tag, Tooltip } from "antd";
import { ExclamationCircleTwoTone } from "@ant-design/icons";
import { UseQueryResult } from "react-query";
import { Inventory } from "../../../api/inventory";
import { Link } from "react-router-dom";
import colors from "../../../css/colors";
import * as S from "./styles";

export const productStatusMap: { [key: string]: string } = {
  Active: "product_status.active",
  Archived: "product_status.inactive",
  Error: "product_status.error",
};

export const productTypeMap: { [key: string]: string } = {
  base: "product_type.base_product",
  kit: "product_type.virtual_kit",
};

export const productColumnsArray = [
  "scid",
  "sku",
  "product_name",
  "description",
  "length",
  "width",
  "height",
  "weight",
  "tariff_code",
  "fragile",
  "product_active",
  "dangerous_good",
  "total_cost",
  "wholesale_cost",
  "retail_cost",
  "upc",
];

export const render = (value: string | number) => Number(value) || 0;

// TODO REFACTOR use product.missing_info
const checkProductHasMissingInfo = (product: Fulfillment.Product): boolean => {
  const { base_products, product_type } = product;
  if (product_type === "kit") return !!base_products?.every((b) => checkProductHasMissingInfo(b));
  const requiredFields = ["width", "length", "height", "weight", "name", "sku", "retail_price"];
  const fieldValues = Object.entries(product)
    .filter(([key]) => requiredFields.includes(key))
    .map(([, value]) => value);
  return fieldValues.every((f) => !f);
};

const checkInvalidField = ({ hs_tariff_code, product_type, base_products }: Fulfillment.Product): boolean => {
  if (product_type === "kit") return !!base_products?.every((b) => checkInvalidField(b));
  return hs_tariff_code?.trim() !== "" && hs_tariff_code?.length !== 10;
};

export const renderIssueToolTip = (product: Fulfillment.Product) => {
  const isMissingInfo = checkProductHasMissingInfo(product);
  const isInvalidField = checkInvalidField(product);
  const isVirtualKit = product?.product_type === "kit";
  if (product?.has_errors) {
    const title = `This ${isVirtualKit ? "virtual kit" : "product"}  has issues that require immediate attention.`;
    return (
      <Tooltip title={title}>
        <ExclamationCircleTwoTone twoToneColor="red" />
      </Tooltip>
    );
  }
  if (isMissingInfo && isInvalidField) {
    const title = isVirtualKit
      ? "This virtual kit contains a product with missing mandatory information and invalid information."
      : "Missing mandatory information and Contains invalid information. We need this information to ensure the best experience for your customers.";
    return (
      <Tooltip title={title}>
        <ExclamationCircleTwoTone twoToneColor="orange" />
      </Tooltip>
    );
  }
  if (isMissingInfo) {
    const title = isVirtualKit
      ? "This virtual kit contains a product with missing mandatory information."
      : "Missing mandatory information. We need this information to determine the best shipment method and rate for your customers.";
    return (
      <Tooltip title={title}>
        <ExclamationCircleTwoTone twoToneColor="orange" />
      </Tooltip>
    );
  }
  if (isInvalidField) {
    const title = isVirtualKit
      ? "This virtual kit contains a product with invalid information."
      : "Contains invalid information. We need this information to ensure the best experience for your customers.";
    return (
      <Tooltip title={title}>
        <ExclamationCircleTwoTone twoToneColor="orange" />
      </Tooltip>
    );
  }
};

export const getColumns = (orgId: string, newInventory: boolean) => [
  {
    title: "Product Name",
    dataIndex: "name",
    key: "name",
    render(name: string, product: Fulfillment.Product) {
      return (
        <S.ProductLink>
          <Link to={`/organizations/${orgId}/products/${product.id}`}>{name}</Link>
        </S.ProductLink>
      );
    },
    sorter: true,
  },
  { title: "SKU", dataIndex: "sku", key: "sku", sorter: true },
  {
    title: "SKU Type",
    dataIndex: "product_type",
    key: "product_type",
    render(value: "kit" | "base") {
      return value === "base" ? "Base Product" : "Virtual Kit";
    },
    sorter: true,
  },
  {
    title: "Status",
    dataIndex: "active",
    key: "active",
    render(active: string, product: Fulfillment.Product) {
      return (
        <Space size={0}>
          <Tag color={active ? colors.GREEN : colors.YELLOW}>{active ? "Active" : "Archived"}</Tag>
          {renderIssueToolTip(product)}
        </Space>
      );
    },
    sorter: true,
  },
  { title: "On Hand", dataIndex: ["inventory", "on_hand"], key: "on_hand", render },
  {
    title: "Available To Pick",
    dataIndex: ["inventory", "available_to_pick"],
    key: "available_to_pick",
    render,
  },
  {
    title: "Available For Sale",
    dataIndex: ["inventory", "available_for_sale"],
    key: "available_for_sale",
    render,
  },
  {
    title: newInventory ? "Processing" : "Reserved",
    dataIndex: ["inventory", newInventory ? "processing" : "reserved"],
    key: newInventory ? "processing" : "reserved",
    render,
  },
  { title: "Incoming", dataIndex: ["inventory", "incoming"], key: "incoming", render },
];

export const useMergedProductInventories = (
  productsQuery: UseQueryResult<{ data: Fulfillment.Product[] }, unknown>,
  inventoriesQuery: UseQueryResult<Inventory[], unknown>
) => {
  return useMemo(() => {
    const products = productsQuery.data?.data || [];
    const inventoryHash = (inventoriesQuery.data || []).reduce(
      (acc, cur) => ({ ...acc, [cur.product_id]: cur }),
      {} as Record<string, Inventory>
    );

    return products.map((product) => ({ ...product, inventory: inventoryHash[product.id] }));
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [productsQuery.dataUpdatedAt, inventoriesQuery.dataUpdatedAt]);
};
